"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { countBand, trackEvent } from "@/lib/analytics";
import { useSession } from "@/lib/auth-client";
import { ApiConfig } from "@/lib/config";
import {
  clearActiveWorkspace,
  dpopApi,
  getActiveWorkspace,
  hasWorkspaceChoice,
  setActiveWorkspace,
} from "@/lib/dpop-client";

// ─── Plans: Fexr Free / Fexr Plus / Fexr Desk ────────────────────────────────
// Contract: fexrapi docs/frontend-billing-v2-handoff.md (product_catalog.py).
// Render limits, prices and periods from the API, never hardcode them.
// `null` limit = no cap. Plus belongs to a person; Desk to a workspace.

export type PlanId = "free" | "plus" | "desk";

export interface PlanLimits {
  reports_per_period: number | null;
  reports_per_day: number | null;
  copilot_per_day: number | null;
  data_calls_per_day: number | null;
  api_requests_per_day: number | null;
  voice_turns_per_day: number | null;
  alert_rules: number | null;
  watchlists: number | null;
  intro_reports: number | null;
  intro_copilot: number | null;
  intelligence_search: boolean;
  workspace_sharing: boolean;
  capital: boolean;
}

export interface UsageMeter {
  used: number;
  limit: number | null;
  /** day = resets 00:00 UTC; period = Plus reports; intro = Free freebies. */
  window: "day" | "period" | "intro";
  resets_at: string | null;
  /** Plus reports only: the per-day cap inside the period allowance. */
  today?: { used: number; limit: number | null; resets_at: string | null };
}

/** GET /v1/billing/plan (honours X-Fexr-Workspace) */
export interface CurrentPlan {
  plan: PlanId;
  plan_name: string;
  scope: "personal" | "workspace";
  workspace_id: string | null;
  period_start: string | null;
  period_end: string | null;
  valid_until: string | null;
  renews: boolean;
  renewal_price_usdc: string | null;
  usage: Partial<Record<"data" | "copilot" | "report" | "voice", UsageMeter>>;
  limits: PlanLimits;
  /** Null before the user has a billing account. */
  credits: {
    fexr_credit_usdc: string;
    alert_credits: number;
    digest_credits: number;
    daily_cap: number | null;
  } | null;
  purchase: { plus: string; desk: string; event_credits: string };
  plans_url?: string;
}

/** One entry of GET /v1/billing/plans */
export interface PlanCatalogEntry {
  plan: PlanId;
  name: string;
  product_id?: string | null;
  price_usdc: string;
  period_days: number | null;
  scope: "personal" | "workspace";
  limits: PlanLimits;
  includes_products: string[];
}

export const PLANS_URL = "https://fexr.io/plans";

export interface Workspace {
  workspace_id: string;
  name: string;
  role: string;
  /** "active", "suspended"…; only an active workspace can be acted as. */
  status?: string;
}

const BILLING_ROLES = new Set(["owner", "admin", "billing_admin"]);

export function canManageBilling(workspace: Workspace | null | undefined) {
  return !!workspace && BILLING_ROLES.has(workspace.role);
}

/**
 * Workspaces the user belongs to (GET /v1/enterprise/workspaces) and the one
 * this device acts as. A user with a workspace is always in one: the first
 * load picks their only workspace (else the first they administer) and
 * reloads once, since earlier requests ran without it. A stored workspace the
 * user no longer belongs to is forgotten and the default picked again.
 */
export function useWorkspaces() {
  const { data: session, status } = useSession();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    setActiveId(getActiveWorkspace());
  }, []);

  useEffect(() => {
    if (status !== "authenticated" || !session?.fexrApiAccessToken) {
      setLoading(status === "loading");
      return;
    }
    let cancelled = false;
    dpopApi
      .get("/v1/enterprise/workspaces", session)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        const items: Workspace[] = Array.isArray(data?.items) ? data.items : [];
        setWorkspaces(items);
        // The API accepts X-Fexr-Workspace only for an active workspace (403
        // otherwise, which forgets the choice and reloads), so never act as
        // a suspended one or the pick → 403 → reload cycle never ends.
        const usable = items.filter((w) => !w.status || w.status === "active");
        const stored = getActiveWorkspace();
        if (stored && !usable.some((w) => w.workspace_id === stored)) {
          clearActiveWorkspace();
          setActiveId(null);
        }
        if (usable.length && !hasWorkspaceChoice()) {
          const pick =
            usable.find((w) => DESK_ADMIN_ROLES.has(w.role)) ?? usable[0]!;
          setActiveWorkspace(pick.workspace_id);
          setActiveId(pick.workspace_id);
          window.location.reload();
        }
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [session, status]);

  const switchTo = useCallback(
    (workspaceId: string | null) => {
      trackEvent("workspace.switched", {
        count_band: countBand(workspaces.length),
      });
      setActiveWorkspace(workspaceId);
      setActiveId(workspaceId);
      // Every cached read (plan, data, research) belongs to the old context.
      window.location.reload();
    },
    [workspaces.length],
  );

  const active = workspaces.find((w) => w.workspace_id === activeId) ?? null;
  return { workspaces, active, loading, switchTo };
}

export function isPaidPlan(plan: PlanId | null | undefined): boolean {
  return plan === "plus" || plan === "desk";
}

let planCatalogCache: Promise<PlanCatalogEntry[] | null> | null = null;

/** `payment_assets[]` from the same response: which tokens checkout accepts. */
export interface PaymentAsset {
  symbol: string;
  address: string;
  decimals: number;
  chain_id: number;
  status: "ready" | "blocked" | string;
}
let paymentAssets: PaymentAsset[] = [];

/** Public plan catalog — fetched once per page load and shared. */
export function fetchPlanCatalog(): Promise<PlanCatalogEntry[] | null> {
  if (!planCatalogCache) {
    planCatalogCache = globalThis
      .fetch(ApiConfig.getApiEndpoint("/v1/billing/plans"))
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (Array.isArray(d?.payment_assets)) paymentAssets = d.payment_assets;
        return Array.isArray(d?.plans) ? d.plans : null;
      })
      .catch(() => null)
      .then((plans) => {
        if (!plans) planCatalogCache = null;
        return plans;
      });
  }
  return planCatalogCache;
}

export function usePlanCatalog() {
  const [plans, setPlans] = useState<PlanCatalogEntry[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchPlanCatalog().then((p) => {
      if (!cancelled) setPlans(p);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const byPlan = useMemo(() => {
    const out: Partial<Record<PlanId, PlanCatalogEntry>> = {};
    for (const p of plans ?? []) out[p.plan] = p;
    return out;
  }, [plans]);
  // Checkout offers only USDC, and only while the API marks it ready (PL-09).
  // An older API without payment_assets is treated as ready.
  const usdc = plans
    ? paymentAssets.find((a) => a.symbol.toUpperCase() === "USDC")
    : undefined;
  const usdcReady = !paymentAssets.length || usdc?.status === "ready";
  return {
    plans,
    byPlan,
    loading: plans === null,
    paymentAssets: plans ? paymentAssets : [],
    usdcReady,
  };
}

export interface FeatureAnnouncement {
  version: number;
  title: string;
  body: string;
  image_url: string | null;
  cta_label: string | null;
  cta_route: string | null;
  accent: string | null;
  min_app_version: string | null;
}

export interface AccountPulse {
  /** Full plan view; null until loaded or when signed out. */
  plan: CurrentPlan | null;
  /** True on Fexr Desk (the active workspace's plan). */
  desk: boolean;
  validUntil: string | null;
  features: Record<string, boolean>;
  hasFeature: (key: string) => boolean;
  announcements: Record<string, FeatureAnnouncement>;
  balance: number | null;
  claimable: number | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  hasNewClaimable: boolean;
  acknowledgeClaimable: () => void;
}

const AccountPulseContext = createContext<AccountPulse | null>(null);
const PULSE_POLL_MS = 90_000;
const CLAIMABLE_SEEN_KEY = "fexr:last-claimable";

function toFiniteNumber(input: unknown): number | null {
  const value = typeof input === "string" ? parseFloat(input) : Number(input);
  return Number.isFinite(value) ? value : null;
}

export function AccountPulseProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: session, status } = useSession();
  const [plan, setPlan] = useState<CurrentPlan | null>(null);
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const [announcements, setAnnouncements] = useState<
    Record<string, FeatureAnnouncement>
  >({});
  const [balance, setBalance] = useState<number | null>(null);
  const [claimable, setClaimable] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seenClaimable, setSeenClaimable] = useState<number | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(CLAIMABLE_SEEN_KEY);
      if (stored !== null) setSeenClaimable(toFiniteNumber(stored));
    } catch {
      // Storage can be unavailable in private browsing.
    }
  }, []);

  const authed = status === "authenticated" && !!session?.fexrApiAccessToken;

  const refresh = useCallback(async () => {
    if (!authed) {
      setPlan(null);
      setFeatures({});
      setAnnouncements({});
      setBalance(null);
      setClaimable(null);
      setError(null);
      setLoading(status === "loading");
      return;
    }

    const settled = await Promise.allSettled([
      dpopApi.get("/v1/billing/plan", session),
      dpopApi.get("/v1/billing/accounts", session),
      dpopApi.get("/v1/users/me/rewards/summary", session),
      dpopApi.get("/v1/users/me/features", session),
    ]);
    if (!mountedRef.current) return;

    const readJson = async (result: PromiseSettledResult<Response>) => {
      if (result.status !== "fulfilled" || !result.value.ok) return null;
      return result.value.json().catch(() => null);
    };
    const [planData, accountsData, rewardsData, featuresData] =
      await Promise.all(settled.map(readJson));
    if (!mountedRef.current) return;

    const accounts = Array.isArray(accountsData)
      ? accountsData
      : (accountsData?.accounts ?? []);

    setPlan(
      planData && typeof planData.plan === "string"
        ? (planData as CurrentPlan)
        : null,
    );

    const rawFeatures = featuresData?.features;
    const resolvedFeatures: Record<string, boolean> = {};
    if (rawFeatures && typeof rawFeatures === "object") {
      for (const [key, value] of Object.entries(rawFeatures)) {
        resolvedFeatures[key] = Boolean(value);
      }
    }
    setFeatures(resolvedFeatures);

    const resolvedAnnouncements: Record<string, FeatureAnnouncement> = {};
    const rawAnnouncements = featuresData?.announcements;
    if (rawAnnouncements && typeof rawAnnouncements === "object") {
      for (const [key, value] of Object.entries(rawAnnouncements) as Array<
        [string, any]
      >) {
        if (
          resolvedFeatures[key] &&
          value &&
          Number(value.version) >= 1 &&
          typeof value.title === "string" &&
          value.title.trim() &&
          typeof value.body === "string" &&
          value.body.trim()
        ) {
          resolvedAnnouncements[key] = value as FeatureAnnouncement;
        }
      }
    }
    setAnnouncements(resolvedAnnouncements);
    setBalance(
      accountsData ? toFiniteNumber(accounts[0]?.current_balance) : null,
    );
    setClaimable(
      rewardsData ? toFiniteNumber(rewardsData.total_pending) : null,
    );
    setError(
      [planData, accountsData, rewardsData, featuresData].some(
        (value) => value === null,
      )
        ? "Some account data could not be loaded."
        : null,
    );
    setLoading(false);
  }, [authed, session, status]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!authed) return;
    const tick = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const interval = window.setInterval(tick, PULSE_POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [authed, refresh]);

  const acknowledgeClaimable = useCallback(() => {
    if (claimable === null) return;
    setSeenClaimable(claimable);
    try {
      window.localStorage.setItem(CLAIMABLE_SEEN_KEY, String(claimable));
    } catch {
      // No-op when storage is unavailable.
    }
  }, [claimable]);

  const hasNewClaimable =
    claimable !== null &&
    claimable > 0 &&
    (seenClaimable === null || claimable > seenClaimable);
  const value = useMemo<AccountPulse>(
    () => ({
      plan,
      desk: plan?.plan === "desk",
      validUntil: plan?.valid_until ?? null,
      features,
      hasFeature: (key) => features[key] === true,
      announcements,
      balance,
      claimable,
      loading,
      error,
      refresh,
      hasNewClaimable,
      acknowledgeClaimable,
    }),
    [
      plan,
      features,
      announcements,
      balance,
      claimable,
      loading,
      error,
      refresh,
      hasNewClaimable,
      acknowledgeClaimable,
    ],
  );

  return (
    <AccountPulseContext.Provider value={value}>
      {children}
    </AccountPulseContext.Provider>
  );
}

function useStandalonePlan(enabled: boolean) {
  const { data: session, status } = useSession();
  const [plan, setPlan] = useState<CurrentPlan | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (
      !enabled ||
      status !== "authenticated" ||
      !session?.fexrApiAccessToken
    ) {
      setPlan(null);
      setLoading(enabled && status === "loading");
      return;
    }
    setLoading(true);
    try {
      const response = await dpopApi.get("/v1/billing/plan", session);
      const data = response.ok ? await response.json() : null;
      setPlan(data && typeof data.plan === "string" ? data : null);
    } catch {
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [enabled, session, status]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { plan, loading, refetch };
}

/**
 * The caller's plan (GET /v1/billing/plan). Fails closed: `plan` is null and
 * `planId` is "free" while loading and on any error, so paid surfaces never
 * flash to Free users.
 */
export function usePlan() {
  const pulse = useContext(AccountPulseContext);
  const standalone = useStandalonePlan(pulse === null);
  const plan = pulse ? pulse.plan : standalone.plan;
  const planId: PlanId = plan?.plan ?? "free";
  return {
    plan,
    planId,
    planName: plan?.plan_name ?? "Fexr Free",
    paid: isPaidPlan(planId),
    desk: planId === "desk",
    loading: pulse ? pulse.loading : standalone.loading,
    refetch: pulse ? pulse.refresh : standalone.refetch,
  };
}

/** Legacy shape: `seat` is true on Fexr Desk only. Prefer usePlan(). */
export function useSeatStatus() {
  const { plan, desk, loading, refetch } = usePlan();
  return {
    seat: desk,
    validUntil: plan?.valid_until ?? null,
    loading,
    refetch,
  };
}

export function useAccountPulse(): AccountPulse | null {
  return useContext(AccountPulseContext);
}

export function useFeature(key: string) {
  const pulse = useContext(AccountPulseContext);
  return {
    enabled: pulse?.features?.[key] === true,
    loading: pulse ? pulse.loading : true,
  };
}

export function useFeatureAnnouncement(key: string) {
  const pulse = useContext(AccountPulseContext);
  if (!pulse || pulse.loading || pulse.features[key] !== true) return null;
  return pulse.announcements[key] ?? null;
}

// ─── Fexr Desk workspace console ─────────────────────────────────────────────
// Shared plumbing for the Settings → Workspace tabs (components/desk/*). Every
// path is under /v1/enterprise/workspaces/{id}; errors are the API's own text.

export const DESK_ADMIN_ROLES = new Set(["owner", "admin"]);
export const DESK_SECURITY_ROLES = new Set([
  "owner",
  "admin",
  "security_admin",
]);
export const DESK_AUDIT_ROLES = new Set([
  "owner",
  "admin",
  "security_admin",
  "auditor",
]);

export function deskErrorText(body: any, status: number): string {
  const d = body?.detail ?? body;
  if (status === 402)
    return "This is part of Fexr Desk. Renew Desk for this workspace.";
  if (typeof d === "string") return d;
  if (d?.message) return String(d.message);
  if (Array.isArray(d))
    return d.map((x: any) => x?.msg ?? String(x)).join("; ");
  if (typeof body?.error_description === "string")
    return body.error_description;
  return `Request failed (${status})`;
}

/** GET a workspace resource; `path` is relative to the workspace base. */
export function useDeskResource<T = any>(
  workspaceId: string | null | undefined,
  path: string | null,
) {
  const { data: session } = useSession();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<number | null>(null);
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const ready = !!session?.fexrApiAccessToken;
  const url =
    path == null
      ? null
      : path.startsWith("/v1/")
        ? path
        : workspaceId
          ? `/v1/enterprise/workspaces/${workspaceId}${path}`
          : null;

  const reload = useCallback(async () => {
    if (!url || !ready) return null;
    setLoading(true);
    try {
      const res = await dpopApi.get(url, sessionRef.current);
      setStatus(res.status);
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        setError(deskErrorText(body, res.status));
        setData(null);
        return null;
      }
      setError(null);
      setData(body as T);
      return body as T;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
      return null;
    } finally {
      setLoading(false);
    }
  }, [url, ready]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { data, loading, error, status, reload };
}

/** Writes against the workspace, with one busy key and the API's error text. */
export function useDeskActions(workspaceId: string) {
  const { data: session } = useSession();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setErrorState] = useState<string | null>(null);
  const lastError = useRef<string | null>(null);
  const setError = useCallback((e: string | null) => {
    lastError.current = e;
    setErrorState(e);
  }, []);
  const base = `/v1/enterprise/workspaces/${workspaceId}`;

  const call = useCallback(
    async <T = any,>(
      key: string,
      method: "post" | "put" | "patch" | "delete",
      path: string,
      body?: unknown,
    ): Promise<T | null> => {
      setBusy(key);
      setError(null);
      const url = path.startsWith("/v1/") ? path : `${base}${path}`;
      try {
        const res =
          method === "delete"
            ? await dpopApi.delete(url, session)
            : await dpopApi[method](url, body ?? {}, session);
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          setError(deskErrorText(data, res.status));
          return null;
        }
        return (data ?? {}) as T;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Request failed");
        return null;
      } finally {
        setBusy(null);
      }
    },
    [base, session, setError],
  );

  return { call, busy, error, setError, base, lastError };
}
